/**
 * 애플리케이션 전역 상수 정의
 */

// ========== 상태 관련 상수 ==========

/** VOC 조치 상태 코드 */
export const ACCEPT_STATUS = {
  PENDING: '0',
  RECEIVED: '1',
  HOLD: '2',
  DELETED: '3',
  COMPLETED: '5'
} as const;

/** VOC 조치 상태 라벨 */
export const ACCEPT_STATUS_LABEL: Record<string, string> = {
  '0': '대기',
  '1': '접수',
  '2': '보류',
  '3': '삭제',
  '5': '종료'
};

/** SERVICE 승인 상태 코드 */
export const SERVICE_STATUS = {
  PENDING: 'pending',
  APPROVED: 'success',
  REJECTED: 'fail',
  CANCELLED: 'cancel'
} as const;

/** SERVICE 승인 상태 라벨 */
export const SERVICE_STATUS_LABEL: Record<string, string> = {
  pending: '대기',
  success: '승인',
  fail: '반려',
  cancel: '취소'
};

// ========== 서비스 분류 상수 ==========

/** 기존VOC 서비스(채널) 분류 코드 */
export const SERVICE_SORTATION: Record<string, string> = {
  '01': '인트라넷',
  '02': '싸다오',
  '03': '아레오',
  '04': '네띠앙',
  '05': '서울이동통신',
  '06': '스탠다드 네트웍스',
  '07': '업체문의',
  '08': '고객사',
  '09': 'UMS',
  '10': '영업부 요청사항',
  '11': 'Say015&SMART015'
};

/** 서비스 타입 코드 */
export const SERVICE_TYPE = {
  MESSAGING: 'messaging',
  AICC: 'aicc'
} as const;

/** 서비스 타입 라벨 */
export const SERVICE_TYPE_LABEL: Record<string, string> = {
  messaging: '메시징',
  aicc: '015웹콜센터'
};

/** 법인 종류 코드 */
export const CORP_TYPE = {
  GENERAL: 'general',
  SPECIAL: 'special',
  RESELLER: 'reseller'
} as const;

/** 법인 종류 라벨 */
export const CORP_TYPE_LABEL: Record<string, string> = {
  general: '일반',
  special: '특수부가통신사업자',
  reseller: '재판매사'
};

// ========== 경과 시간 관련 상수 ==========

/** 경과 시간 상태 */
export const ELAPSED_STATUS = {
  LEVEL1: 'Elapsed1', // 0-3시간 (초록)
  LEVEL2: 'Elapsed2', // 3-6시간 (주황)
  LEVEL3: 'Elapsed3' // 6시간 초과 (빨강)
} as const;

/** 경과 시간 임계값 (초 단위) */
export const ELAPSED_THRESHOLDS = {
  LEVEL1_MAX: 10800, // 3시간
  LEVEL2_MAX: 21600, // 6시간
  ONE_DAY: 86400 // 24시간
} as const;

/** 경과 상태별 스타일 */
export const ELAPSED_STYLES: Record<string, { bg: string; border: string; color: string }> = {
  Elapsed1: { bg: '#f6fffb', border: '#b7efd6', color: '#0b8a4a' },
  Elapsed2: { bg: '#fffaf6', border: '#ffd8b5', color: '#c56a00' },
  Elapsed3: { bg: '#fff5f5', border: '#ffbdbd', color: '#b32a2a' }
};

/** 승인 상태별 스타일 */
export const STATUS_BADGE_STYLES: Record<string, { bg: string; color: string }> = {
  대기: { bg: '#fef3c7', color: '#92400e' },
  승인: { bg: '#dcfce7', color: '#166534' },
  반려: { bg: '#fee2e2', color: '#991b1b' }
};

// ========== 탭 관련 상수 ==========

/** 탭 타입 */
export const TAB_TYPE = {
  VOC: 'VOC',
  SERVICE: 'SERVICE'
} as const;

// ========== 검색 관련 상수 ==========

/** VOC 탭 검색 옵션 */
export const VOC_SEARCH_OPTIONS = [
  { value: '제목', label: '제목' },
  { value: '작성자', label: '작성자' },
  { value: '조치담당자', label: '담당자' }
] as const;

/** SERVICE 탭 검색 옵션 */
export const SERVICE_SEARCH_OPTIONS = [
  { value: '제목', label: '제목' },
  { value: '법인명', label: '법인명' },
  { value: '사업자등록번호', label: '사업자등록번호' },
  { value: '작성자명', label: '작성자명' }
] as const;

/** VOC 조치 상태 필터 옵션 */
export const VOC_STATUS_OPTIONS = [
  { value: '4', label: '전체' },
  { value: '0', label: '대기' },
  { value: '1', label: '접수' },
  { value: '2', label: '보류' },
  { value: '3', label: '삭제' },
  { value: '5', label: '종료' }
] as const;

/** SERVICE 서비스 종류 필터 옵션 */
export const SERVICE_TYPE_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'messaging', label: '메시징' },
  { value: 'aicc', label: '015웹콜센터' }
] as const;

/** SERVICE 승인 상태 필터 옵션 */
export const SERVICE_STATUS_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'pending', label: '대기' },
  { value: 'success', label: '승인' },
  { value: 'fail', label: '반려' },
  { value: 'cancel', label: '취소' }
] as const;

/** 메시징 법인종류 옵션 */
export const MESSAGING_CORP_TYPE_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'messaging-general', label: '일반' },
  { value: 'messaging-special', label: '특수부가통신사업자' }
] as const;

/** AICC 법인종류 옵션 */
export const AICC_CORP_TYPE_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'aicc-general', label: '일반' },
  { value: 'aicc-reseller', label: '재판매사' }
] as const;

/** 기본 법인종류 옵션 */
export const DEFAULT_CORP_TYPE_OPTIONS = [{ value: 'all', label: '전체' }] as const;

// ========== 페이지네이션 기본값 ==========

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE = 0;

// ========== 서비스 신청 상세 관련 상수 ==========

/** 서비스 타입 맵 (상세페이지용) */
export const SERVICE_TYPE_DISPLAY_MAP: Record<string, string> = {
  messaging: '메시징 서비스',
  aicc: '015웹콜센터 서비스'
};

/** 사업자 유형 맵 */
export const BUSINESS_TYPE_MAP: Record<string, string> = {
  general: '일반',
  special: '특수부가통신사업자',
  reseller: '재판매사'
};

/** 파일 코드와 라벨 매핑 */
export const FILE_CODE_LABEL_MAP: Record<string, string> = {
  businessLicense: '사업자등록증 사본',
  employmentCert: '재직증명서',
  corporateSealCert: '법인 인감 증명서',
  telecomCert: '통신사이용증명원',
  callerIdDoc: '발신번호 등록서류',
  callerIdConsent: '발신번호 변작방지 및 사전동의서',
  specialTelecomCert: '특수부가통신사업자 등록증',
  transmissionCert: '전송자격인증서'
};

/** 메시지 발송 목적 옵션 */
export const MESSAGE_PURPOSE_OPTIONS = [
  { value: 'info', label: '정보성 알림' },
  { value: 'marketing', label: '영업 및 마케팅 광고' },
  { value: 'notice', label: '기관 안내 및 공지' },
  { value: 'other', label: '기타' }
] as const;

/** 회사 유형 옵션 (메시징 서비스용) */
export const COMPANY_TYPE_OPTIONS = [
  { value: 'reseller', label: '문자 재판매사' },
  { value: 'hosting', label: '문자 호스팅' },
  { value: 'outsourcing', label: '문자 발송대행사 운영' },
  { value: 'agency', label: '대행 외' },
  { value: 'solution', label: '솔루션' },
  { value: 'idc', label: 'IDC 서비스' },
  { value: 'webagency', label: '웹 에이전시' },
  { value: 'other', label: '기타' }
] as const;

/** 서비스 타입과 사업자 유형에 따른 파일 코드 설정 */
export const FILE_CODES_CONFIG: Record<string, Record<string, string[]>> = {
  messaging: {
    general: [
      'businessLicense',
      'employmentCert',
      'corporateSealCert',
      'telecomCert',
      'callerIdDoc',
      'callerIdConsent'
    ],
    special: [
      'businessLicense',
      'corporateSealCert',
      'specialTelecomCert',
      'transmissionCert',
      'callerIdDoc'
    ]
  },
  aicc: {
    general: [
      'businessLicense',
      'employmentCert',
      'corporateSealCert',
      'telecomCert',
      'callerIdDoc',
      'callerIdConsent'
    ],
    reseller: [
      'businessLicense',
      'corporateSealCert',
      'specialTelecomCert',
      'transmissionCert',
      'callerIdDoc'
    ]
  }
};

// ========== 유틸리티 함수 ==========

/** corpType에서 businessType 추출 */
export const getBusinessTypeFromCorpType = (corpType: string): string => {
  if (!corpType) return 'general';
  const parts = corpType.split('-');
  return parts.length > 1 ? parts[1] : 'general';
};

/** 상태 표시 변환 */
export const getStatusDisplay = (status: string): string => {
  const statusLower = status?.toLowerCase() || '';
  if (['pending', '0', '대기'].includes(statusLower)) return '대기';
  if (['success', 'approved', '1', '승인'].includes(statusLower)) return '승인';
  if (['fail', 'rejected', '2', '반려', '거절'].includes(statusLower)) return '거절';
  return status || '-';
};

/** 날짜 포맷 (YYYYMMDDHHMMSS -> YYYY-MM-DD HH:MM:SS) */
export const formatDateTime = (dateStr: string): string => {
  if (!dateStr || dateStr.length < 8) return dateStr || '-';
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);
  const hour = dateStr.substring(8, 10) || '00';
  const min = dateStr.substring(10, 12) || '00';
  const sec = dateStr.substring(12, 14) || '00';
  return `${year}-${month}-${day} ${hour}:${min}:${sec}`;
};

/** vendorType 파싱 (JSON string -> array) */
export const parseVendorType = (vendorType: string): string[] => {
  if (!vendorType) return [];
  try {
    // Remove escape slashes and trim
    let s = vendorType.replace(/\\/g, '').trim();

    // Try parsing as JSON first
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed;
      // If parsed is a string that itself looks like an array
      if (typeof parsed === 'string' && parsed.trim().startsWith('[')) {
        s = parsed.trim();
      } else if (typeof parsed === 'string') {
        // comma-separated string
        return parsed
          .split(',')
          .map(p => p.trim().replace(/^['"]|['"]$/g, ''))
          .filter(Boolean);
      }
    } catch {
      // fallthrough to manual parsing
    }

    // If the string looks like [a, b] or ["a","b"]
    if (s.startsWith('[') && s.endsWith(']')) {
      const inner = s.substring(1, s.length - 1).trim();
      if (!inner) return [];
      return inner
        .split(',')
        .map(t => t.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean);
    }

    // If it's a comma-separated list without brackets
    if (s.includes(',')) {
      return s
        .split(',')
        .map(p => p.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean);
    }

    // Single token
    return [s.replace(/^['"]|['"]$/g, '')].filter(Boolean);
  } catch {
    return [];
  }
};

/** msgPurpose 파싱 (JSON string -> array) */
export const parseMsgPurpose = (msgPurpose: string): string[] => {
  if (!msgPurpose) return [];
  try {
    // Remove escape slashes and trim
    let s = msgPurpose.replace(/\\/g, '').trim();

    // If it is a JSON value already, try parsing
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed;
      // If parsed is a string that itself looks like an array (e.g. "[a,b]")
      if (typeof parsed === 'string' && parsed.trim().startsWith('[')) {
        s = parsed.trim();
      } else if (typeof parsed === 'string') {
        // comma-separated string like "info, marketing"
        return parsed
          .split(',')
          .map(p => p.trim().replace(/^['"]|['"]$/g, ''))
          .filter(Boolean);
      }
    } catch {
      // fallthrough to manual parsing below
    }

    // If the string looks like [a, b] or ["a","b"]
    if (s.startsWith('[') && s.endsWith(']')) {
      const inner = s.substring(1, s.length - 1).trim();
      if (!inner) return [];
      // split by comma and normalize tokens
      return inner
        .split(',')
        .map(t => t.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean);
    }

    // If it's a comma-separated list without brackets
    if (s.includes(',')) {
      return s
        .split(',')
        .map(p => p.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean);
    }

    // Single token
    return [s.replace(/^['"]|['"]$/g, '')].filter(Boolean);
  } catch {
    return [];
  }
};
